import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Award, Star, Send, CheckCircle, Clock, Github, Globe, Code, MessageSquare, LogOut } from 'lucide-react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Score Slider Component (extracted to avoid nested component issues)
const ScoreSlider = ({ label, value, onChange, weight }) => (
  <div className="mb-4">
    <div className="flex justify-between items-center mb-2">
      <label className="text-sm font-semibold text-gray-700">{label}</label>
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500">Weight: {(weight * 100).toFixed(0)}%</span>
        <span className="bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-bold">{value}</span>
      </div>
    </div>
    <input
      type="range"
      min="1"
      max="10"
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value))}
      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
    />
    <div className="flex justify-between text-xs text-gray-400 mt-1">
      <span>Poor (1)</span>
      <span>Excellent (10)</span>
    </div>
  </div>
);

const JuryPanel = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [juryName, setJuryName] = useState('');
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [scoring, setScoring] = useState(false);

  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [scoreForm, setScoreForm] = useState({
    innovation: 5,
    technical_implementation: 5,
    ui_ux_design: 5,
    presentation: 5,
    impact: 5,
    comments: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('jury_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setIsLoggedIn(true);
      setJuryName(localStorage.getItem('jury_name') || '');
      fetchAssignments();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('username', loginForm.username);
      formData.append('password', loginForm.password);

      const response = await axios.post(`${API}/jury/login`, formData);
      localStorage.setItem('jury_token', response.data.token);
      localStorage.setItem('jury_name', response.data.name);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      
      setIsLoggedIn(true);
      setJuryName(response.data.name);
      toast.success(`Welcome, ${response.data.name}!`);
      fetchAssignments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('jury_token');
    localStorage.removeItem('jury_name');
    delete axios.defaults.headers.common['Authorization'];
    setIsLoggedIn(false);
    setAssignments([]);
  };

  const fetchAssignments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/jury/my-assignments`);
      setAssignments(response.data.assignments || []);
    } catch (err) {
      console.error('Error fetching assignments:', err);
      if (err.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleScoreSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSubmission) return;

    setScoring(true);
    try {
      await axios.post(`${API}/jury/score/${selectedSubmission.id}`, scoreForm);
      toast.success('Score submitted successfully!');
      setSelectedSubmission(null);
      setScoreForm({
        innovation: 5,
        technical_implementation: 5,
        ui_ux_design: 5,
        presentation: 5,
        impact: 5,
        comments: ''
      });
      fetchAssignments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit score');
    } finally {
      setScoring(false);
    }
  };

  // Login Form
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 flex items-center justify-center px-4">
        <Toaster position="top-right" />
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <div className="bg-purple-100 p-4 rounded-full w-fit mx-auto mb-4">
              <Award className="text-purple-600" size={40} />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">Jury Panel</h1>
            <p className="text-gray-600 mt-2">Login to evaluate hackathon submissions</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Username</label>
              <input
                type="text"
                value={loginForm.username}
                onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Jury Dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-6 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Award size={32} />
            <div>
              <h1 className="text-xl font-bold">Jury Panel</h1>
              <p className="text-purple-200">Welcome, {juryName}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto py-8 px-4">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <p className="text-gray-600 text-sm">Assigned</p>
            <p className="text-3xl font-bold text-purple-600">{assignments.length}</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <p className="text-gray-600 text-sm">Scored</p>
            <p className="text-3xl font-bold text-green-600">{assignments.filter(a => a.already_scored).length}</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <p className="text-gray-600 text-sm">Pending</p>
            <p className="text-3xl font-bold text-yellow-600">{assignments.filter(a => !a.already_scored).length}</p>
          </div>
        </div>

        {/* Submissions List */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-xl font-bold text-gray-800">Your Assigned Submissions</h2>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent mx-auto"></div>
            </div>
          ) : assignments.length === 0 ? (
            <div className="p-12 text-center">
              <Clock className="mx-auto text-gray-300 mb-4" size={48} />
              <p className="text-gray-500">No submissions assigned yet</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {assignments.map(submission => (
                <div key={submission.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-bold text-lg text-gray-800">{submission.project_title}</h3>
                        {submission.already_scored ? (
                          <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                            <CheckCircle size={12} /> Scored
                          </span>
                        ) : (
                          <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                            <Clock size={12} /> Pending
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 text-sm mb-2">Team: {submission.team_name}</p>
                      <p className="text-gray-500 text-sm line-clamp-2">{submission.project_description}</p>
                      
                      <div className="flex flex-wrap gap-2 mt-3">
                        {submission.tech_stack?.map((tech, idx) => (
                          <span key={idx} className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs">
                            {tech}
                          </span>
                        ))}
                      </div>

                      <div className="flex gap-4 mt-3">
                        {submission.github_url && (
                          <a href={submission.github_url} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-purple-600 flex items-center gap-1 text-sm">
                            <Github size={16} /> GitHub
                          </a>
                        )}
                        {submission.demo_url && (
                          <a href={submission.demo_url} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-purple-600 flex items-center gap-1 text-sm">
                            <Globe size={16} /> Demo
                          </a>
                        )}
                      </div>
                    </div>

                    {!submission.already_scored && (
                      <button
                        onClick={() => setSelectedSubmission(submission)}
                        className="bg-purple-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-purple-700 flex items-center gap-2"
                      >
                        <Star size={18} />
                        Score
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Scoring Modal */}
      {selectedSubmission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-t-2xl">
              <h2 className="text-xl font-bold">Score Submission</h2>
              <p className="text-purple-200 mt-1">{selectedSubmission.project_title}</p>
              <p className="text-purple-300 text-sm">Team: {selectedSubmission.team_name}</p>
            </div>

            <form onSubmit={handleScoreSubmit} className="p-6 space-y-6">
              <div className="bg-purple-50 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-purple-800 mb-2">Scoring Weights</h4>
                <p className="text-purple-600 text-sm">
                  Innovation & Technical Implementation have higher weights (25% each). 
                  Impact has 20% weight, while UI/UX and Presentation have 15% each.
                </p>
              </div>

              <ScoreSlider
                label="Innovation"
                value={scoreForm.innovation}
                onChange={(v) => setScoreForm({ ...scoreForm, innovation: v })}
                weight={0.25}
              />

              <ScoreSlider
                label="Technical Implementation"
                value={scoreForm.technical_implementation}
                onChange={(v) => setScoreForm({ ...scoreForm, technical_implementation: v })}
                weight={0.25}
              />

              <ScoreSlider
                label="UI/UX Design"
                value={scoreForm.ui_ux_design}
                onChange={(v) => setScoreForm({ ...scoreForm, ui_ux_design: v })}
                weight={0.15}
              />

              <ScoreSlider
                label="Presentation & Demo"
                value={scoreForm.presentation}
                onChange={(v) => setScoreForm({ ...scoreForm, presentation: v })}
                weight={0.15}
              />

              <ScoreSlider
                label="Impact / Real-world Usefulness"
                value={scoreForm.impact}
                onChange={(v) => setScoreForm({ ...scoreForm, impact: v })}
                weight={0.20}
              />

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <MessageSquare className="inline mr-2" size={16} />
                  Comments (optional)
                </label>
                <textarea
                  value={scoreForm.comments}
                  onChange={(e) => setScoreForm({ ...scoreForm, comments: e.target.value })}
                  placeholder="Add any feedback or comments for this submission..."
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 resize-none"
                  rows={3}
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setSelectedSubmission(null)}
                  className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={scoring}
                  className="flex-1 bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {scoring ? 'Submitting...' : (
                    <>
                      <Send size={18} />
                      Submit Score
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default JuryPanel;
